// Traffic Simulation — dry-run route testing (W9).
//
// Backend: POST /api/campaigns/{cid}/simulate — emulates N clicks through
// the campaign's flows using operator-supplied sample datasets and returns
// the routing decision + per-stream filter trace for each, WITHOUT recording
// any real click. Page lives at /campaigns/:campaignId/simulate.

import React, { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const DATASET_FIELDS = [
  ['ips', 'IPs', '8.8.8.8\n1.1.1.1'],
  ['user_agents', 'User agents', 'Mozilla/5.0 (iPhone)...\nHeadlessChrome'],
  ['referrers', 'Referrers', 'https://google.com'],
  ['keywords', 'Keywords', 'buy shoes'],
  ['languages', 'Languages', 'en\nru'],
  ['sources', 'Sources', 'fb\ngoogle'],
  ['sub_ids', 'Sub IDs', 'sid1\nsid2'],
  ['parameters', 'Parameters', 'utm_campaign=promo&sub2=x'],
];

export default function TrafficSimulation() {
  const { campaignId } = useParams();
  const { t } = useT();
  const [queries, setQueries] = useState(100);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { queries: Number(queries) || 100 };
      for (const [key] of DATASET_FIELDS) {
        if (fields[key] && fields[key].trim()) payload[key] = fields[key];
      }
      const res = await axios.post(`${API}/campaigns/${campaignId}/simulate`, payload);
      setResult(res.data);
    } catch (e) {
      toast.error(t('Simulation failed'));
    } finally {
      setLoading(false);
    }
  }, [campaignId, queries, fields, t]);

  const summary = result?.summary;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('Traffic Simulation')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('Dry-run clicks through this campaign to test flow logic and filters — no real traffic is recorded.')}
          </p>
        </div>
        <Link to={`/campaigns/${campaignId}/streams`}>
          <Button variant="outline">{t('Back to streams')}</Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('Sample datasets')}</CardTitle>
          <CardDescription>
            {t('One value per line (or comma-separated). Each simulated query samples one value from each list.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {DATASET_FIELDS.map(([key, label, placeholder]) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`sim-${key}`}>{t(label)}</Label>
                <Textarea
                  id={`sim-${key}`}
                  rows={3}
                  placeholder={placeholder}
                  value={fields[key] || ''}
                  onChange={(e) => setField(key, e.target.value)}
                  data-testid={`sim-field-${key}`}
                />
              </div>
            ))}
          </div>
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="sim-queries">{t('Queries')}</Label>
              <Input
                id="sim-queries"
                type="number"
                min="1"
                max="1000"
                value={queries}
                onChange={(e) => setQueries(e.target.value)}
                className="w-32"
              />
            </div>
            <Button onClick={run} disabled={loading} data-testid="sim-run">
              {loading ? t('Simulating…') : t('Run simulation')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>{t('Distribution')}</CardTitle>
            <CardDescription>
              {t('Total')}: {summary.total} · {t('Bots')}: {summary.bots} · {t('No match')}: {summary.no_match}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <h3 className="text-sm font-medium mb-1">{t('By stream')}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1">{t('Stream')}</th>
                    <th className="py-1">{t('Clicks')}</th>
                    <th className="py-1">{t('Share')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_stream.map((s) => (
                    <tr key={String(s.stream_id)} className="border-t">
                      <td className="py-1">{s.stream_name || t('(no match)')}</td>
                      <td className="py-1">{s.count}</td>
                      <td className="py-1">
                        {summary.total ? Math.round((s.count / summary.total) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground">
              {t('By destination')}:{' '}
              {Object.entries(summary.by_destination || {})
                .map(([k, v]) => `${k}: ${v}`)
                .join(' · ')}
            </div>
          </CardContent>
        </Card>
      )}

      {result?.results?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('Per-query trace')}</CardTitle>
            <CardDescription>{t('First 25 simulated clicks and how each was routed.')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-2">#</th>
                    <th className="py-1 pr-2">{t('Country')}</th>
                    <th className="py-1 pr-2">{t('Device')}</th>
                    <th className="py-1 pr-2">{t('Bot')}</th>
                    <th className="py-1 pr-2">{t('Stream')}</th>
                    <th className="py-1 pr-2">{t('Destination')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.slice(0, 25).map((r) => (
                    <tr key={r.query} className="border-t align-top">
                      <td className="py-1 pr-2">{r.query}</td>
                      <td className="py-1 pr-2">{r.country_code || '—'}</td>
                      <td className="py-1 pr-2">{r.device_type || '—'}</td>
                      <td className="py-1 pr-2">{r.is_bot ? '✓' : ''}</td>
                      <td className="py-1 pr-2">{r.stream_name || t('(no match)')}</td>
                      <td className="py-1 pr-2">
                        {r.destination?.type}
                        {r.destination?.target
                          ? ` → ${Array.isArray(r.destination.target) ? r.destination.target.join(', ') : r.destination.target}`
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
